/**
 * Kartta — the pivots on a compass whose walls are the room's scenarios (#594).
 *
 * Position is meaning: angle from the scenario mix, distance from how evenly
 * the scenarios share the pivot, size from convergence, colour from
 * direction, the thin ring the scenario shares, the rim the map hanging
 * below. Glyphs are leverage. Arcs are the map's own: a loop through two
 * pivots, a cross-link, a confirmed sama asia — never similarity.
 * Plain SVG, no simulation: the same data gives the same picture every tick.
 */
import React, { useMemo, useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import styled from '@emotion/styled';
import { DIRECTION } from '../PivotCard';
import { EFFECT_TYPE_LABELS_FI } from '../../config/effectTypes';
import {
  SIZE, CENTRE, R_OUTER, R_INNER, rad,
  compassAxes, layoutPivots, pivotEdges, loopsByPivot, layoutMembers, wrapWords,
} from './compassLayout';

const INK = '#e8e8ef'; const MUTED = '#8e93a8'; const FAINT = '#4b4f63'; const LINE = '#262638'; const GROUND = '#0f0f16';
const LOOP = { up: '#e0a82e', down: '#e0533f' };
const VAL = { wanted: '#3ecf8e', unwanted: '#e0708a', unknown: '#5c6079' };

const Svg = styled.svg`
  width: 100%; height: 100%; display: block; touch-action: none;
  .ring { fill: none; stroke: ${LINE}; stroke-width: 1; }
  .ringlabel { fill: ${FAINT}; font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; }
  .axis { font-weight: 700; font-size: 13px; letter-spacing: 0.14em; text-transform: uppercase; }
  .centre { fill: ${INK}; font-weight: 700; font-size: 15px; }
  .centre2 { fill: ${MUTED}; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; }
  .node { cursor: pointer; outline: none; }
  .node .core { transition: filter 0.18s ease; }
  .node:hover .core, .node.sel .core, .node:focus-visible .core { filter: brightness(1.18); }
  .halo { fill: none; stroke: ${INK}; stroke-width: 1.5; stroke-dasharray: 3 4; opacity: 0; transition: opacity 0.18s; }
  .node.sel .halo { opacity: 1; }
  .label { fill: ${INK}; font-weight: 600; font-size: 12.5px; paint-order: stroke; stroke: ${GROUND}; stroke-width: 4px; stroke-linejoin: round; }
  .glyph { font-size: 11px; font-weight: 700; fill: ${GROUND}; }
  .edge { fill: none; stroke-width: 2; opacity: 0.85; }
  .edge.loop { stroke: ${LOOP.down}; stroke-dasharray: 7 5; }
  .edge.cross { stroke: #b7bcd3; stroke-width: 1.6; }
  .edge.same { stroke: #7ea6ff; stroke-dasharray: 4 3; }
  .edgelabel { fill: ${LOOP.down}; font-size: 11px; font-weight: 600; paint-order: stroke; stroke: ${GROUND}; stroke-width: 4px; }
  .dim { opacity: 0.22; transition: opacity 0.18s; }
  .chain { fill: none; stroke: #5a5f78; stroke-width: 1.2; opacity: 0.9; }
  .chain.cross { stroke: #b7bcd3; stroke-width: 1.6; }
  .chain.same { stroke: #7ea6ff; stroke-dasharray: 4 3; }
  .member circle { stroke: ${GROUND}; stroke-width: 1.5; }
  &.members .label { opacity: 0.35; }
  &.wall .axis { font-size: 18px; } &.wall .label { font-size: 17px; } &.wall .centre { font-size: 20px; } &.wall .ringlabel { font-size: 14px; }
  .mark-grown { fill: none; stroke: ${LOOP.up}; stroke-width: 2; stroke-dasharray: 2 4; }
  .mark-new { fill: none; stroke: ${INK}; stroke-width: 2; }
`;
const Frame = styled.div`position: relative; width: 100%; height: 100%;`;
const ZoomBar = styled.div`
  position: absolute; right: 10px; bottom: 10px; display: flex; gap: 4px; z-index: 2;
  button { width: 30px; height: 30px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.14); background: rgba(20,20,30,0.85); color: #ddd; font: inherit; font-size: 1rem; cursor: pointer; }
  button:hover { background: rgba(255,255,255,0.1); }
  button:focus-visible { outline: 1px solid #a78bfa; }
`;

/* Which pivots a legend row lights. A row is a predicate on the candidate;
   the compass dims the rest exactly as it dims non-neighbours of a selection. */
export const HIGHLIGHTS = {
  attractor:   (c) => c.direction === 'attractor',
  opportunity: (c) => c.direction === 'opportunity',
  contested:   (c) => c.direction === 'contested',
  aiOnly:      (c) => !!c.leverage?.direction && !c.leverage.direction.humanJudged,
  loop:        (c) => ((c.leverage?.facts?.reinforcingLoops || 0) + (c.leverage?.facts?.balancingLoops || 0)) > 0,
  choice:      (c) => !!c.leverage?.facts?.choicePoint,
  cross:       (c) => (c.leverage?.facts?.crossLinks || 0) > 0,
  blocker:     (c) => (c.leverage?.facts?.blockers || 0) > 0,
  structural:  (c) => c.lane === 'rakenteellinen',
};

const DIAG = Math.SQRT1_2; // the ring labels sit on the north-east diagonal, off the axes
const arcPath = (r, a0, a1) => {
  const p = (a) => [r * Math.cos(a), r * Math.sin(a)];
  const [x0, y0] = p(a0); const [x1, y1] = p(a1);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1} ${y1}`;
};
const bowPath = (a, b, bow) => {
  const mx = (a.x + b.x) / 2; const my = (a.y + b.y) / 2;
  const dx = b.x - a.x; const dy = b.y - a.y; const len = Math.hypot(dx, dy) || 1;
  return { d: `M ${a.x} ${a.y} Q ${mx - (dy / len) * bow} ${my + (dx / len) * bow} ${b.x} ${b.y}`, lx: mx - (dy / len) * bow * 0.55, ly: my + (dx / len) * bow * 0.55 };
};

export default function PivotCompass({
  candidates = [], nodesById = {}, scenarios = [], loops = [], connections = [],
  question = '', selectedId = null, onSelect, showMembers = false, wall = false, marks = {},
  // A legend row's key (HIGHLIGHTS): the matching pivots stay bright, the rest dim.
  highlight = null,
}) {
  const svgRef = useRef(null);
  const zoomRef = useRef(null);
  const [transform, setTransform] = useState(d3.zoomIdentity);
  // Wheel, pinch and drag, as on the map. The compass is a fixed viewBox, so
  // the zoom applies to one inner group and the SVG itself never moves.
  useEffect(() => {
    if (!svgRef.current) return undefined;
    const svg = d3.select(svgRef.current);
    const zoom = d3.zoom().scaleExtent([0.6, 6]).filter((e) => !e.button && !(e.type === 'wheel' && e.ctrlKey === false && e.shiftKey))
      .on('zoom', (e) => setTransform(e.transform));
    zoomRef.current = zoom;
    svg.call(zoom).on('dblclick.zoom', null);
    return () => { svg.on('.zoom', null); };
  }, []);
  const zoomBy = (k) => { if (svgRef.current && zoomRef.current) d3.select(svgRef.current).transition().duration(200).call(zoomRef.current.scaleBy, k); };
  const zoomReset = () => { if (svgRef.current && zoomRef.current) d3.select(svgRef.current).transition().duration(250).call(zoomRef.current.transform, d3.zoomIdentity); };
  const lit = highlight && HIGHLIGHTS[highlight] ? HIGHLIGHTS[highlight] : null;
  const axes = useMemo(() => compassAxes(scenarios), [scenarios]);
  const placed = useMemo(() => layoutPivots(candidates, nodesById, axes), [candidates, nodesById, axes]);
  const edges = useMemo(() => pivotEdges(placed, loops, connections), [placed, loops, connections]);
  const loopsBy = useMemo(() => loopsByPivot(placed, loops), [placed, loops]);
  const members = useMemo(() => (showMembers ? layoutMembers(placed, nodesById, axes) : null), [showMembers, placed, nodesById, axes]);
  const byId = useMemo(() => Object.fromEntries(placed.map((n) => [n.c.id, n])), [placed]);

  const neighbours = useMemo(() => {
    const s = new Set();
    if (selectedId) for (const e of edges) { if (e.a === selectedId) s.add(e.b); if (e.b === selectedId) s.add(e.a); }
    return s;
  }, [edges, selectedId]);
  const toggle = (id) => onSelect?.(selectedId === id ? null : id);
  const rings = [[R_OUTER, 'yhdessä skenaariossa'], [R_INNER + (R_OUTER - R_INNER) * 0.5, 'kahdessa'], [R_INNER, 'kaikissa']];
  const k = wall ? 1.35 : 1;          // the wall's type is bigger, so its spacing is too
  const lh = 15 * k;                  // label line height
  const questionLines = wrapWords(question, 24, 3);

  return (
    <Frame>
    <Svg ref={svgRef} viewBox={`0 0 ${SIZE} ${SIZE}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Vipupistekartta"
      className={`${showMembers ? 'members' : ''} ${wall ? 'wall' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget || e.target === e.currentTarget.firstChild) onSelect?.(null); }}>
    <g transform={transform.toString()}>
      {rings.map(([r, l]) => (
        <g key={l}><circle cx={CENTRE} cy={CENTRE} r={r} className="ring" /><text x={CENTRE + r * DIAG + 6} y={CENTRE - r * DIAG - 6} className="ringlabel">{l}</text></g>
      ))}
      {Object.values(axes).map((a) => {
        const cos = Math.cos(rad(a.angle)); const sin = Math.sin(rad(a.angle));
        // A wall's name must not sit where a node's label on that axis goes:
        // north and south beyond the labels, east and west above the line.
        const sideways = Math.abs(cos) > 0.5;
        const x = CENTRE + (R_OUTER + (sideways ? 8 : 52)) * cos; const y = CENTRE + (R_OUTER + 52) * sin;
        const anchor = sideways ? (cos > 0 ? 'start' : 'end') : 'middle';
        const ty = sideways ? CENTRE - 30 * k : sin < 0 ? y : y + 16 * k;
        const wy = sideways ? CENTRE - 46 * k : sin < 0 ? y + 16 * k : y;
        return (
          <g key={a.id}>
            <line x1={CENTRE} y1={CENTRE} x2={CENTRE + R_OUTER * cos} y2={CENTRE + R_OUTER * sin} className="ring" />
            <text x={x} y={ty} className="axis" textAnchor={anchor} fill={a.color}>{a.title.toUpperCase()}</text>
            {a.wall && <text x={x} y={wy} className="ringlabel" textAnchor={anchor}>{a.wall}</text>}
          </g>
        );
      })}
      <g>
        {questionLines.map((ln, i) => <text key={i} x={CENTRE} y={CENTRE - 6 - (questionLines.length - 1) * 10 + i * 20} className="centre" textAnchor="middle">{ln}</text>)}
        <text x={CENTRE} y={CENTRE + 14 + (questionLines.length - 1) * 10 + 6} className="centre2" textAnchor="middle">pääkysymys</text>
      </g>

      {/* the map's own arcs between pivots */}
      <g className={selectedId && !neighbours.size ? 'dim' : ''}>
        {edges.map((e) => {
          const a = byId[e.a]; const b = byId[e.b]; if (!a || !b) return null;
          const len = Math.hypot(b.x - a.x, b.y - a.y);
          const { d, lx, ly } = bowPath(a, b, Math.min(90, len * 0.35));
          const dim = (selectedId && e.a !== selectedId && e.b !== selectedId) || (lit && !(lit(byId[e.a]?.c || {}) && lit(byId[e.b]?.c || {})));
          const parts = [];
          if (e.loops.length) parts.push(`↻ ${e.loops[0].title || 'silmukka'}${e.loops.length > 1 ? ` +${e.loops.length - 1}` : ''}`);
          if (e.cross.length) parts.push(`⇄ ${[...new Set(e.cross.map((t) => EFFECT_TYPE_LABELS_FI[t] || t))].join(', ')}`);
          if (e.same) parts.push('sama asia');
          const loopColor = e.loops.length ? LOOP[e.loops[0].direction] || LOOP.down : null;
          return (
            <g key={`${e.a}|${e.b}`} className={dim ? 'dim' : ''}>
              <path d={d} className={`edge ${e.kind}`} style={loopColor ? { stroke: loopColor } : undefined} />
              <text x={lx} y={ly + 4} className="edgelabel" textAnchor="middle" style={{ fill: loopColor || MUTED }}>{parts.join(' · ')}</text>
            </g>
          );
        })}
      </g>

      {/* Jäsenet: dots on the side of their scenario, real chains between them */}
      {members && (
        <g>
          {members.chains.map((ch, i) => {
            const { d } = bowPath(ch.from, ch.to, ch.cross ? Math.min(60, Math.hypot(ch.to.x - ch.from.x, ch.to.y - ch.from.y) * 0.25) : 0);
            return <path key={`c${i}`} d={d} className={`chain${ch.cross ? ' cross' : ''}`} />;
          })}
          {connections.map((c, i) => {
            const a = members.pos[c.parentId]; const b = members.pos[c.childId]; if (!a || !b) return null;
            return <line key={`l${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} className={`chain ${c.type === 'is_same' || c.type === 'multi-parent' ? 'same' : 'cross'}`} />;
          })}
          {Object.values(members.pos).map((m) => (
            <g key={m.node.id} className="member" transform={`translate(${m.x},${m.y})`}>
              <circle r={4.5} fill={VAL[m.node.valence] || VAL.unknown} />
              <title>{m.node.text} — {axes[m.node.scenarioId]?.title || ''}</title>
            </g>
          ))}
        </g>
      )}

      <g>
        {placed.map((n) => {
          const { c, x, y, size, shares, ang } = n;
          const dir = DIRECTION[c.direction] || DIRECTION.contested;
          const f = c.leverage?.facts || {};
          const aiOnly = c.leverage?.direction && !c.leverage.direction.humanJudged;
          const stroke = 2 + Math.min(6, Math.log2(1 + (f.descendants || 0)) * 1.2);
          const total = Object.values(shares).reduce((a, b) => a + b, 0) || 1;
          const dim = (selectedId && c.id !== selectedId && !neighbours.has(c.id)) || (lit && !lit(c));
          const glyphs = [];
          for (const l of loopsBy[c.id] || []) glyphs.push({ t: '↻', color: LOOP[l.direction] || LOOP.down, title: `silmukka: ${l.title || ''}` });
          if (f.choicePoint) glyphs.push({ t: '⑂', color: '#0099ff', title: 'valintakohta' });
          if (f.crossLinks) glyphs.push({ t: '⇄', color: '#8e9bb3', title: `${f.crossLinks} ristiinyhteys` });
          if (f.blockers) glyphs.push({ t: '⛔', color: '#e0533f', title: `${f.blockers} este` });
          const lines = wrapWords(c.title, wall ? 18 : 20, 3);
          const left = Math.cos(ang) < -0.15; const lx = left ? -(size + 14) : size + 14;
          let a0 = -Math.PI / 2;
          const mark = marks[c.id];
          return (
            <g key={c.id} className={`node${c.id === selectedId ? ' sel' : ''}${dim ? ' dim' : ''}`} transform={`translate(${x},${y})`}
              tabIndex={0} role="button" aria-label={c.title} aria-pressed={c.id === selectedId}
              onClick={() => toggle(c.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(c.id); } }}>
              <title>{c.rank}. {c.title}{aiOnly ? ' — suunta tekoälyn arvioista, ei vielä ihmisen valenssia' : ''}</title>
              <circle r={size + 10} className="halo" />
              {mark === 'new' && (
                <circle r={size + 12} className="mark-new">
                  <animate attributeName="r" values={`${size + 10};${size + 30}`} dur="1.6s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.9;0" dur="1.6s" repeatCount="indefinite" />
                </circle>
              )}
              {mark === 'grown' && <circle r={size + 12} className="mark-grown" />}
              {c.direction === 'contested' ? (
                <>
                  <circle r={size} fill="#1a1a26" stroke="#2e2e40" strokeWidth={stroke} className="core" />
                  <path d={arcPath(size, rad(-90), rad(90))} fill="none" stroke={DIRECTION.attractor.color} strokeWidth={stroke} strokeDasharray={aiOnly ? '5 4' : undefined} />
                  <path d={arcPath(size, rad(90), rad(270))} fill="none" stroke={DIRECTION.opportunity.color} strokeWidth={stroke} strokeDasharray={aiOnly ? '5 4' : undefined} />
                </>
              ) : (
                <circle r={size} fill={dir.bg} stroke={dir.color} strokeWidth={stroke} strokeDasharray={aiOnly ? '5 4' : undefined} className="core" />
              )}
              {Object.entries(shares).map(([sid, cnt]) => {
                const ax = axes[sid]; if (!ax) return null;
                const a1 = a0 + (2 * Math.PI * cnt) / total; const d = arcPath(size + 6, a0 + 0.04, a1 - 0.04); a0 = a1;
                return <path key={sid} d={d} fill="none" stroke={ax.color} strokeWidth={3} strokeLinecap="round" />;
              })}
              <text y={-2} textAnchor="middle" fill={INK} style={{ fontWeight: 700, fontSize: Math.round(size * 0.7) }}>{c.rank}</text>
              {size >= 36 && <text y={size * 0.42} textAnchor="middle" fill={MUTED} style={{ fontSize: 10, letterSpacing: '0.06em' }}>{c.convergence?.participants ?? '–'} hlö · {c.convergence?.scenarios ?? '–'} sk</text>}
              {glyphs.map((g, i) => {
                const a = rad(35 + i * 32);
                return (
                  <g key={i} transform={`translate(${(size + 6) * Math.cos(a)},${(size + 6) * Math.sin(a)})`}>
                    <circle r={9} fill={g.color} stroke={GROUND} strokeWidth={2} />
                    <text y={4} textAnchor="middle" className="glyph">{g.t}</text>
                    <title>{g.title}</title>
                  </g>
                );
              })}
              {lines.map((ln, i) => <text key={i} x={lx} y={-((lines.length - 1) * lh) / 2 + i * lh + 4} className="label" textAnchor={left ? 'end' : 'start'}>{ln}</text>)}
            </g>
          );
        })}
      </g>
    </g>
    </Svg>
    {!wall && (
      <ZoomBar aria-label="Zoomaus">
        <button type="button" title="Lähennä" onClick={() => zoomBy(1.4)}>+</button>
        <button type="button" title="Loitonna" onClick={() => zoomBy(1 / 1.4)}>−</button>
        <button type="button" title="Palauta" onClick={zoomReset}>⌂</button>
      </ZoomBar>
    )}
    </Frame>
  );
}
